import { observable, computed, decorate, action, flow } from 'mobx'
import axios from 'axios'

/**
 * Describes the store containing al info about the authenticated user.
 */
class AuthenticatedUserStore {
    static ACTIONS = {
        READ: 'news:article:read',
        WRITE: 'news:article:write',
        DELETE: 'news:article:delete',
        WRITE_INCIDENT: 'news:incident:write',
        DELETE_INCIDENT: 'news:incident:delete',
        CREATE_SECURITY_ARTICLE: 'news:security-vulnerabilities-article:crud',
        CSA_NOTIFICATION_READ: 'news:csa-notification-article:read',
        CSA_NOTIFICATION_CRUD: 'news:csa-notification-article:crud',
    }

    static IMPERSONATE = {
        ORGANIZATION: 'organization',
    }

    isAllowed = action => {
        if (
            this.permissions.indexOf(AuthenticatedUserStore.ACTIONS[action]) !==
            -1
        ) {
            return true
        }
        return false
    }

    constructor() {
        /**
         * @Observable
         * @type {object}
         */
        this.userData = {}
        this.authToken = ''
        this.permissions = []
        this.impersonated = {}
        this.responses = {}
        this.loading = {}
        this.loadingPromises = {}
    }

    /**
     * @computed
     * @returns {string}
     */
    get email() {
        return this.userData.email
    }

    /**
     * @computed
     * @returns {string}
     */
    get firstName() {
        return this.userData.firstName
    }

    /**
     * @computed
     * @returns {string}
     */
    get lastName() {
        return this.userData.lastName
    }
    /**
     * @computed
     * @returns {string}
     */
    get username() {
        return this.userData.username
    }

    /**
     * @computed
     * @returns {boolean}
     */
    get isLocalUser() {
        return this.userData.is_local_user
    }

    /**
     * @computed
     * @returns {string}
     */
    get impersonatedOrganization() {
        const { ORGANIZATION } = AuthenticatedUserStore.IMPERSONATE
        return this.impersonated[ORGANIZATION]?.label
    }

    /**
     * @computed
     * @returns {string}
     */
    get impersonatedOrganizationId() {
        const { ORGANIZATION } = AuthenticatedUserStore.IMPERSONATE
        return this.impersonated[ORGANIZATION]?.value
    }

    /**
     * Returns the currently active organization ID, either from the impersonated organization, or from the user.
     * @return {*|string|number}
     */
    get activeOrganizationId() {
        return this.impersonatedOrganizationId ?? this.userData.organization_id
    }

    /**
     * @type {Function & IAction}
     */
    setUserData = action(data => {
        this.userData = data
        if (!this.userData.is_local_user) {
            this.loadUserOrganizations()
        }
    })

    setImpersonatedOrganization = action(organization => {
        const { ORGANIZATION } = AuthenticatedUserStore.IMPERSONATE
        this.impersonated[ORGANIZATION] = organization
    })

    loadUserOrganizations = flow(function* () {
        const { ORGANIZATION } = AuthenticatedUserStore.IMPERSONATE
        this.loading[ORGANIZATION] = true
        try {
            this.loadingPromises[ORGANIZATION] = axios.get(
                `/api/auth/users/${this.userData.username}/tenants`
            )
            this.responses[ORGANIZATION] =
                yield this.loadingPromises[ORGANIZATION]
        } catch (ex) {
            return Promise.reject(ex)
        } finally {
            this.loading[ORGANIZATION] = false
        }
    })

    impersonateOrganization = flow(function* (organization) {
        try {
            const response = yield axios.post('/api/auth/impersonator', {
                organization_id: organization.value,
            })
            this.setAuthToken(response.headers.authorization)
            this.setImpersonatedOrganization(organization)
            this.loadPermissions(this.userData.username, organization.value)
        } catch (e) {
            console.error(e)
            return Promise.reject(e)
        }
    })

    stopImpersonateOrganization = flow(function* () {
        try {
            const response = yield axios.delete('/api/auth/impersonator', {
                params: {
                    organization_only: true,
                },
            })
            this.setAuthToken(response.headers.authorization)
            this.setImpersonatedOrganization(null)
            this.loadPermissions(this.userData.username, null)
        } catch (e) {
            console.error(e)
            return Promise.reject(e)
        }
    })

    /**
     * @type {Function & IAction}
     */
    setAuthToken = action(token => {
        this.authToken = token
        axios.defaults.headers.common.Authorization = token
    })

    /**
     * @type {Function & IAction}
     */
    setPermissions = action(permissions => {
        this.permissions = permissions
    })

    loadPermissions = flow(function* (user, organization) {
        try {
            let params = {}
            if (organization) {
                params.organization_id = organization
            }
            const response = yield axios.get(
                `/api/auth/users/${user}/app-permissions`,
                {
                    params,
                }
            )
            const { app_permissions } = response.data
            if (!app_permissions || app_permissions.length === 0) {
                this.setPermissions([])
            } else {
                this.setPermissions(app_permissions[0].actions)
            }
        } catch (e) {
            return Promise.reject(e)
        }
    })

    get userOrganizations() {
        let parsedResponse = []
        const { ORGANIZATION } = AuthenticatedUserStore.IMPERSONATE
        if (this.responses[ORGANIZATION] && this.responses[ORGANIZATION].data) {
            parsedResponse = this.responses[ORGANIZATION].data.tenants
        }
        return parsedResponse
    }

    get userOrganization() {
        const tenant = this.userOrganizations.find(
            tenant => tenant.organization_id === this.userData.organization_id
        )
        return tenant?.name || ''
    }
}

export default decorate(AuthenticatedUserStore, {
    userData: observable,
    authToken: observable,
    permissions: observable,
    email: computed,
    username: computed,
    firstName: computed,
    lastName: computed,
    loading: observable,
    responses: observable,
    impersonatedOrganization: computed,
    impersonatedOrganizationId: computed,
    isLocalUser: computed,
    userOrganizations: computed,
    userOrganization: computed,
    activeOrganizationId: computed,
})
